import cx from 'classnames';
import { useCallback, useMemo } from "react";
import { ExternalListingRow_ExternalListingFragment, ListingsSection_AdFragment } from "../../../../codegen/graphql";
import { checkLeboncoinListingVariationsConfigurationCompleteness, ExternalListingConfiguration, filterVariationsByConfiguration, isValueExcludedByType, VariationConfigurationType, VariationConfigurationValue, VariationConfigurationVariation } from "../../../../common/listingVariationsConfiguration";
import { extractAdFormConfig } from "../../../lib/leboncoin/LeboncoinExtApi";
import { LeboncoinAttributesForm } from "../LeboncoinAttributesForm";
import Button from "../../common/Button";
import { SearchSelect } from "../../common/SearchSelect/SearchSelect";
import { Toggle } from "../../common/Toggle";
import EditListingConfigurationModal_VariationRow from './EditListingConfigurationModal_VariationRow';
import SetMarginOrTargetPrice from '../../common/SetMarginOrTargetPrice';
import { getTranslatedValue } from '../../common/TranslatedValue';


export default function EditListingConfigurationModal_Variations({
  listingConfigurationWithDefaults,
  adData,
  leboncoinAdFormConfig,
  updateListingConfiguration,
  listing,
  importTemuExternalListingIds,
  importAliexpressExternalListingIds,
  configurationCheck,
}: {
  listingConfigurationWithDefaults: ExternalListingConfiguration;
  adData: ListingsSection_AdFragment;
  leboncoinAdFormConfig: ReturnType<typeof extractAdFormConfig> | null;
  updateListingConfiguration: (input: Partial<ExternalListingConfiguration>) => void;
  listing: ExternalListingRow_ExternalListingFragment;
  importTemuExternalListingIds: () => void;
  importAliexpressExternalListingIds: () => void;
  configurationCheck: ReturnType<typeof checkLeboncoinListingVariationsConfigurationCompleteness>;
}) {
  /* 
   * Types / type values / variations setters
   */
  const updateTypeConfiguration = useCallback((typeId: string, updates: Partial<VariationConfigurationType>) => {
    updateListingConfiguration({
      variationsConfig: {
        ...listingConfigurationWithDefaults.variationsConfig,
        types: {
          ...listingConfigurationWithDefaults.variationsConfig.types,
          [typeId]: {
            ...listingConfigurationWithDefaults.variationsConfig.types[typeId],
            ...updates,
          },
        },
      }
    });
  }, [listingConfigurationWithDefaults, updateListingConfiguration]);

  const updateValueConfiguration = useCallback((valueId: string, updates: Partial<VariationConfigurationValue>) => {
    updateListingConfiguration({
      variationsConfig: {
        ...listingConfigurationWithDefaults.variationsConfig,
        values: {
          ...listingConfigurationWithDefaults.variationsConfig.values,
          [valueId]: {
            ...listingConfigurationWithDefaults.variationsConfig.values[valueId],
            ...updates,
          },
        },
      }
    });
  }, [listingConfigurationWithDefaults, updateListingConfiguration]);

  const updateVariationConfiguration = useCallback((variationId: string, updates: Partial<VariationConfigurationVariation>) => {
    updateListingConfiguration({
      variationsConfig: {
        ...listingConfigurationWithDefaults.variationsConfig,
        variations: {
          ...listingConfigurationWithDefaults.variationsConfig.variations,
          [variationId]: {
            ...listingConfigurationWithDefaults.variationsConfig.variations[variationId],
            ...updates,
          },
        },
      }
    });
  }, [listingConfigurationWithDefaults, updateListingConfiguration]);

  // Leboncoin specific forms
  // Get Leboncoin variations types from the ad form config
  const leboncoinVariationsTypes = useMemo(() => {
    if (
      leboncoinAdFormConfig?.handlesVariations
      && leboncoinAdFormConfig
      && leboncoinAdFormConfig.variationsItemsKeys
    ) {
      return leboncoinAdFormConfig.variationsItemsKeys.map((typeKey: any) => (
        leboncoinAdFormConfig.variationsItems.find((item: any) => item.identifier === typeKey)
      )).map((definition: any) => (
        {
          label: definition.decoration.label,
          key: definition.answer_modelization.representation.associated_key,
          definition,
        }
      )) 
    }

    return [];
  }, [leboncoinAdFormConfig?.handlesVariations, leboncoinAdFormConfig?.variationsItemsKeys, leboncoinAdFormConfig?.variationsItems]);

  // Render the leboncoin type form
  // let the user map the type to a leboncoin type key
  const renderLeboncoinTypeForm = (typeId: string, extraData: any, onChange: (data: any) => void) => (
    <select
      value={extraData?.leboncoin_type_key}
      onChange={(e) => onChange({
        leboncoin_type_key: e.target.value
      })}
    >
      <option value="">Select a variation type</option>
      {leboncoinVariationsTypes.map((variationType: any) => (
        <option key={variationType.key} value={variationType.key}>{variationType.label}</option>
      ))}
    </select>
  );


  // Render the leboncoin type value form
  // let the user map the type value to a leboncoin type value key
  const renderLeboncoinTypeValueForm = (
    typeConfig: VariationConfigurationType,
    typeValueId: string,
    extraData: VariationConfigurationValue['extraData'],
    onChange: (data: any) => void,
  ) => {
    if (!typeConfig.extraData?.leboncoin_type_key) {
      return (
        <p className="text-sm text-gray-700">
          Please select a leboncoin type key
        </p>
      );
    }

    const definition = leboncoinVariationsTypes.find((variationType: any) => (
      variationType.key === typeConfig.extraData?.leboncoin_type_key
    ))?.definition;

    if (!definition) {
      return null;
    }

    return (
      <LeboncoinAttributesForm
        fieldsToDisplay={[definition.identifier]}
        fieldsDefinition={[definition]}
        data={{
          ...listing.externalData?.mainAttributes,
          ...extraData,
        }}
        onChange={(data) => onChange({
          ...extraData,
          ...data
        })}
      />
    );
  }
  
  const displayTypeForm = useCallback((typeId: string, extraData: any, onChange: (data: any) => void) => {
    if (listing.shop.shopType === 'leboncoin') {
      return renderLeboncoinTypeForm(typeId, extraData, onChange);
    }

    return null;
  }, [listing.shop.shopType, renderLeboncoinTypeForm]);

  const displayTypeValueForm = useCallback((typeConfig: VariationConfigurationType, typeValueId: string, extraData: VariationConfigurationValue['extraData'], onChange: (data: any) => void) => {
    if (listing.shop.shopType === 'leboncoin') {
      return renderLeboncoinTypeValueForm(typeConfig, typeValueId, extraData, onChange);
    }
  }, [listing.shop.shopType, renderLeboncoinTypeValueForm]);

  const handleTypeValueSelect = useCallback((typeId: string, value: { id: string; value: string } | null) => {
    updateTypeConfiguration(typeId, {
      useSingleValueId: value?.id || null,
    });
  }, [updateTypeConfiguration]);

  const filteredVariations = useMemo(() => {
    return filterVariationsByConfiguration(adData.variations, listingConfigurationWithDefaults);
  }, [adData.variations, listingConfigurationWithDefaults]);



  return (<>
    <div className="flex flex-row gap-2">
      {/* <TextInput
        label="Margin percent on base cost"
        type="number"
        fullWidth={false}
        placeholder={(() => {
          const marginPercent = inheritMarginPercentOnBaseCost({
            supplierType: listingConfigurationWithDefaults.supplierKey as SupplierName,
            variationPercent: null,
            typeValuesPercent: null,
            listingPercent: listingConfigurationWithDefaults.marginPercentOnBaseCost,
            adPercent: adData.marginPercentOnBaseCost,
          })
          return `${marginPercent.label}: ${marginPercent.percent}%`
        })()}
        defaultValue={listingConfigurationWithDefaults.marginPercentOnBaseCost || ''}
        onBlur={(e) => {
          updateListingConfiguration('marginPercentOnBaseCost', parseInt(e.target.value) || null);
        }}
      />
       */}
      <SetMarginOrTargetPrice
        setForKey={`listing-${listing.id}`}
        data={{
          listing: listing,
          listingConfiguration: listingConfigurationWithDefaults,
          variation: null,
          ad: adData,
        }}
        onUpdate={updateListingConfiguration}
      />
      <div className="flex-1" />
      {listing.externalId && listing.shop.shopType === 'temu' && (
        <Button onClick={importTemuExternalListingIds} className="self-end">
          Import Temu external listing IDs
        </Button>
      )}
      {listing.externalId && listing.shop.shopType === 'aliexpress' && (
        <Button onClick={importAliexpressExternalListingIds} className="self-end">
          Import Aliexpress external listing IDs
        </Button>
      )}
    </div>


    <div className="flex flex-row gap-2">
      <div style={{ width: 400 }}>
        {/* Types and Values Section */}
        <div className="flex items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Types and Values</h3>
        </div>

        {/* Types and Values */}
        <div className="space-y-4">
          {adData.variationTypes.map((type) => (
            <div key={type.id} className="border rounded-lg overflow-hidden">
              {/* Type Header */}
              <div className="bg-gray-50 px-4 py-3 flex items-center gap-4">
                <p className="text-xl font-bold text-gray-700">{getTranslatedValue(type.type)}</p>
                <div className="flex-1" />
                <div className="col-span-2">
                  {displayTypeForm?.(type.id, listingConfigurationWithDefaults.variationsConfig.types[type.id]?.extraData, (data) => updateTypeConfiguration(type.id, { extraData: data }))}
                  {configurationCheck.typesErrors[type.id] && (
                    <p className="text-sm text-orange-700">
                      {configurationCheck.typesErrors[type.id].reason}
                    </p>
                  )}
                </div>
                <SearchSelect
                  value={
                    listingConfigurationWithDefaults.variationsConfig.types[type.id]?.useSingleValueId
                      ? type.typeValues.find(v => v.id === listingConfigurationWithDefaults.variationsConfig.types[type.id]?.useSingleValueId) || null
                      : null
                  }
                  loadOptions={async (searchText) => {
                    const filtered = type.typeValues.filter(v => 
                      getTranslatedValue(v.value).toLowerCase().includes(searchText.toLowerCase())
                    );
                    return [{ id: '', value: 'Keep All' }, ...filtered];
                  }}
                  renderOption={(option, selectCallback) => (
                    <div
                      key={option.id}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={selectCallback}
                    >
                      {typeof option.value === 'string' ? option.value : getTranslatedValue(option.value)}
                    </div>
                  )}
                  renderSelected={(option) => typeof option.value === 'string' ? option.value : getTranslatedValue(option.value)}
                  onSelect={(option) => handleTypeValueSelect(type.id, option.id === '' ? null : option)}
                  onClear={() => handleTypeValueSelect(type.id, null)}
                  placeholder="Keep All"
                  withSearch={false}
                />
              </div>

              {/* Type Values */}
              {/* Grid Header */}
              <div 
                className="grid grid-cols-12 gap-2 px-2 py-2 bg-gray-50 border-b border-gray-200"
              >
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider col-span-1">
                  KEEP
                </div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider col-span-6">
                  Valeur
                </div>
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wide col-span-1">
                  EXTRA
                </div>
              </div>
              
              {/* Grid Rows */}
              <div className="divide-y divide-gray-200">
                {type.typeValues.map((value) => {
                  const valueConfig = listingConfigurationWithDefaults.variationsConfig.values[value.id];
                  const typeConfig = listingConfigurationWithDefaults.variationsConfig.types[type.id];
                  const isExcludedByType = isValueExcludedByType(type.id, value.id, listingConfigurationWithDefaults);

                  return (<div>
                    {valueConfig?.include === false && (
                      <p className="col-span-6 text-sm text-gray-700 px-2 pt-2 bg-gray-100">
                        This value is excluded by itself
                      </p>
                    )}
                    {isExcludedByType && (
                      <p className="col-span-6 text-sm text-gray-700 px-2 pt-2 bg-gray-100">
                        This value is excluded by the parent type using a different single value
                      </p>
                    )}

                    {configurationCheck.valuesErrors[value.id] && (
                      <p className="col-span-6 text-sm text-orange-700 px-2 pt-2 bg-gray-100">
                        {configurationCheck.valuesErrors[value.id].reason}
                      </p>
                    )}

                    <div 
                      key={value.id} 
                      className={`grid grid-cols-12 gap-2 px-2 py-2 items-center ${
                        isExcludedByType ? 'bg-gray-100' : 
                        !valueConfig?.include ? 'bg-gray-100' : ''
                      }`}
                    >
                      <div className="col-span-1 flex items-center">
                        <Toggle
                          label=""
                          value={
                            typeConfig?.useSingleValueId === value.id
                              ? true
                              : valueConfig?.include ?? true
                          }
                          onChange={(include) => {
                            if (typeConfig?.useSingleValueId === value.id) {
                              // Cannot be changed if the type is using a single value
                              updateValueConfiguration(value.id, { include: true });
                            } else {
                              updateValueConfiguration(value.id, { include });
                            }
                          }}
                          disabled={isExcludedByType}
                        />
                      </div>

                      {adData.typeIdUsedForImages === type.id && (  
                        <div className="col-span-1">
                          <img
                            src={value.medias?.[0]?.url || '/placeholder.png'}
                            className="w-10 h-10 object-contain rounded-md border border-gray-200"
                            alt="Value"
                          />
                        </div>
                      )}

                      <p className={cx(
                        "text-xl font-medium",
                        adData.typeIdUsedForImages === type.id
                          ? 'col-span-5'
                          : 'col-span-6'
                      )}>
                        {getTranslatedValue(value.value)}
                      </p>
                      
                      <div className="col-span-5 flex flex-col items-stretch">
                        {displayTypeValueForm?.(listingConfigurationWithDefaults.variationsConfig.types[type.id], value.id, valueConfig?.extraData, (data) => updateValueConfiguration(value.id, { extraData: data }))}
                      </div>
                    </div>
                  </div>);
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1">
        {/* Variations List */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">
            {filteredVariations.length} variations{' '}
            {filteredVariations.length !== adData.variations.length && (
              <span className="text-gray-500">
                ({adData.variations.length - filteredVariations.length} excluded)
              </span>
            )}
          </h3>
        </div>

        <div className="overflow-x-auto">
          {/* Grid Header */}
          <div 
            className="grid gap-2 px-3 py-3 bg-gray-50 border-b border-gray-200"
            style={{
              gridTemplateColumns: `repeat(${7 + adData.variationTypes.length * 2}, minmax(0, 1fr))`
            }}
          >
            <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Keep
            </div>
            <div className="col-span-1 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Image
            </div>
            {adData.variationTypes.map(type => (
              <div
                key={type.id}
                className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {getTranslatedValue(type.type)}
              </div>
            ))}
            <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
              Price
            </div>
            
            <div className="col-span-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
              More
            </div>
          </div>
          
          {/* Grid Rows */}
          <div className="divide-y divide-gray-200">
            {adData.variations.map((variation) => {
              return (
                <EditListingConfigurationModal_VariationRow
                  key={variation.id}
                  adData={adData}
                  listing={listing}
                  variation={variation}
                  listingConfigurationWithDefaults={listingConfigurationWithDefaults}
                  updateVariationConfiguration={updateVariationConfiguration}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  </>)
}